import { Form, Stack } from 'react-bootstrap';
import React, { useEffect, useRef } from 'react';
import { useClickOutside, useShallowEffect } from '@mantine/hooks';

import Common from '../../../stores/Common';
import { IDocumentModel } from '../../../interfaces/ICase';
import { IValidation } from '../../../interfaces/IError';
import classNames from 'classnames';
import styles from './ExternalDocumentEditor.module.scss';

export interface IExternalDocumentViewModel extends IDocumentModel {
    editGuid: string;
    isValid?: boolean;
}

export function ExternalDocumentEditor(props: {
    disabled?: boolean;
    document: IExternalDocumentViewModel;
    onExternalDocumentChange: (document: IExternalDocumentViewModel) => void;
    removeIconComponent: React.ReactNode;
}) {
    if (!props.document.editGuid) return null;

    const containerRef = useClickOutside(() => validate());
    const nameInputRef = React.useRef<HTMLInputElement>(null);

    const [localDocument, setLocalDocument] = React.useState<IExternalDocumentViewModel>(
        props.document
    );
    const [validation, setValidation] = React.useState<IValidation>({});

    const validate = () => {
        const name = localDocument.fileName ?? '';
        const uri = localDocument.externalURL ?? '';

        const nameIsMissing = name.length === 0;
        const uriIsMissing = uri.length === 0;
        const uriIsValid = !uriIsMissing && Common.isValidUri(uri);

        const documentNameErrors = nameIsMissing ? ['Required'] : [];

        let documentUriErrors: string[] = [];
        if (uriIsMissing) {
            documentUriErrors = ['Required'];
        } else if (!uriIsValid) {
            documentUriErrors = ['Invalid URL'];
        }

        const isValid = !nameIsMissing && uriIsValid;

        setLocalDocument((prev) => ({
            ...prev,
            isValid,
        }));

        setValidation((prev) => ({
            ...prev,
            documentName: documentNameErrors,
            documentUri: documentUriErrors,
        }));
    };

    const handleContainerBlur = (e: React.FocusEvent) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) {
            validate();
        }
    };

    const onLocalDocumentNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalDocument((prev) => ({ ...prev, fileName: e.target.value }));
    };

    const onLocalDocumentUriChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalDocument((prev) => ({ ...prev, externalURL: e.target.value }));
    };

    useShallowEffect(() => {
        props.onExternalDocumentChange(localDocument);
    }, [localDocument]);

    useEffect(() => {
        containerRef.current && nameInputRef.current?.focus();
    }, []);

    return (
        <div style={{ position: 'relative' }}>
            <div ref={containerRef} onBlur={handleContainerBlur}>
                <Stack
                    direction="horizontal"
                    gap={2}
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                    }}
                >
                    <div style={{ width: 250 }}>
                        <input
                            disabled={props.disabled}
                            type="text"
                            className={classNames('form-control w-100', styles.input, styles.fluid)}
                            value={localDocument.fileName ?? ''}
                            placeholder="Document Name"
                            onChange={onLocalDocumentNameChange}
                            onBlur={onLocalDocumentNameChange}
                            ref={nameInputRef}
                        />
                    </div>
                    <div style={{ flexGrow: 1 }}>
                        <input
                            disabled={props.disabled}
                            type="text"
                            className={classNames('form-control w-100', styles.input, styles.fluid)}
                            value={localDocument.externalURL ?? ''}
                            placeholder="Document URL"
                            onChange={onLocalDocumentUriChange}
                            onBlur={onLocalDocumentUriChange}
                        />
                    </div>
                    <div style={{ minWidth: 60 }}>
                        {localDocument.isValid && (
                            <i className="fas fa-check-circle text-success" />
                        )}
                    </div>
                </Stack>
                <Stack direction="horizontal" gap={2} style={{ marginTop: 2 }}>
                    <div style={{ width: 250, fontSize: '0.9em' }}>
                        {validation.documentName && (
                            <span className="text-danger font-small">
                                {validation.documentName}
                            </span>
                        )}
                    </div>
                    <div style={{ flexGrow: 1, fontSize: '0.9em' }}>
                        {validation.documentUri && (
                            <span className="text-danger">{validation.documentUri}</span>
                        )}
                    </div>
                    <div style={{ minWidth: 60 }}></div>
                </Stack>
            </div>
            <div
                style={{
                    marginRight: 0,
                    position: 'absolute',
                    right: -10,
                    top: 7,
                }}
            >
                {props.removeIconComponent}
            </div>
        </div>
    );
}
